import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Table, Badge, Card, Row, Col } from "react-bootstrap";
import Pagination from 'react-js-pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUser, faEnvelope, faPhone, faMapMarkerAlt, faCalendarAlt, faTrash, faCheckCircle, faBan, faUserEdit } from '@fortawesome/free-solid-svg-icons';

import ViewItemsPerPage from "../ViewItemsPerPage";

interface ListUsersProps {
  usersList: any[];
  usersListCount: number;
  usersListPageIndex: number;
  usersListPageSize: number;
  onChangePageIndex: (pageNumber: number) => void;
  onChangePageSize: (pageSize: number) => void;
  findOptions: any;
  handleFindUser: (findOptions: any) => void;
  resetFindOptions: () => void;
  toggleActivation: (userId: number, userDetails: any) => void;
  onDeleteUser: (userId: number) => void;
}

const ListUsers: React.FC<ListUsersProps> = ({
  usersList,
  usersListCount,
  usersListPageIndex,
  usersListPageSize,
  onChangePageIndex,
  onChangePageSize,
  findOptions,
  handleFindUser,
  resetFindOptions,
  toggleActivation,
  onDeleteUser,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [searchOptions, setSearchOptions] = useState(findOptions);

  const handleModalClose = () => setShowModal(false);
  const handleModalOpen = () => setShowModal(true);

  const onChangeFieldValue = (value: object) => {
    setSearchOptions({ ...searchOptions, ...value });
  }
  const onClickFind = (findOptions: any) => {
    handleFindUser(findOptions);
    handleModalClose();
  };

  useEffect(() => { setSearchOptions(findOptions) }, [findOptions])

  return (
    <div className="customer-list-container">
      <style>{`
        .customer-list-container {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .modern-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          background: #fff;
          overflow: hidden;
        }
        .modern-table {
          margin-bottom: 0;
        }
        .modern-table thead th {
          background-color: #f8f9fa;
          color: #495057;
          font-weight: 700;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e9ecef;
          padding: 1rem;
        }
        .modern-table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #f1f3f5;
        }
        .user-avatar {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          background: linear-gradient(135deg, #198754 0%, #20c997 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          margin-right: 15px;
          box-shadow: 0 4px 10px rgba(32, 201, 151, 0.3);
          flex-shrink: 0;
        }
        .icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .icon-btn:hover {
          transform: translateY(-2px);
        }
        /* Mobile Card Inspiration Styles */
        .mobile-inspiration-card {
          background: #fff;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.06);
        }
        .mobile-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        .mobile-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
        }
        .mobile-card-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .mobile-card-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 4px;
        }
        .mobile-card-subtitle {
          font-size: 0.9rem;
          color: #4a5568;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .mobile-card-desc {
          font-size: 0.85rem;
          color: #718096;
          line-height: 1.4;
          margin-bottom: 15px;
        }
        .mobile-card-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .mobile-pill {
          background: #f7fafc;
          border: 1px solid #edf2f7;
          border-radius: 20px;
          padding: 6px 14px;
          font-size: 0.75rem;
          color: #4a5568;
          font-weight: 600;
        }
      `}</style>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: '#2d3748' }}>Customer Directory</h4>
          <p className="text-muted mb-0 small">Manage your registered customers efficiently.</p>
        </div>
        <Button variant="primary" className="shadow-sm rounded-pill px-4" onClick={handleModalOpen} style={{ background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)', border: 'none' }}>
          <FontAwesomeIcon icon={faSearch} className="me-2" /> Find Options
        </Button>
      </div>

      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Find Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="find-customer-id">
              <Form.Label className="small fw-semibold text-muted">CUSTOMER ID</Form.Label>
              <Form.Control
                type="text"
                className="rounded-3"
                value={searchOptions.userId ? searchOptions.userId : ''}
                onChange={(e) => onChangeFieldValue({ userId: parseInt(e.target.value) })}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="find-full-name">
              <Form.Label className="small fw-semibold text-muted">NAME</Form.Label>
              <Form.Control
                type="text"
                className="rounded-3"
                value={searchOptions.name ? searchOptions.name : ''}
                onChange={(e) => onChangeFieldValue({ name: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-email">
                  <Form.Label className="small fw-semibold text-muted">EMAIL</Form.Label>
                  <Form.Control
                    type="email"
                    className="rounded-3"
                    value={searchOptions.email ? searchOptions.email : ''}
                    onChange={(e) => onChangeFieldValue({ email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-mobile">
                  <Form.Label className="small fw-semibold text-muted">MOBILE</Form.Label>
                  <Form.Control
                    type="text"
                    className="rounded-3"
                    value={searchOptions.mobile ? searchOptions.mobile : ''}
                    onChange={(e) => onChangeFieldValue({ mobile: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" className="rounded-pill px-4" onClick={() => resetFindOptions()}>Clear</Button>
          <Button variant="success" className="rounded-pill px-4 shadow-sm" onClick={() => onClickFind(searchOptions)}>Apply Search</Button>
        </Modal.Footer>
      </Modal>

      <div className="d-none d-md-block">
        <Card className="modern-card mb-4">
          <div className="table-responsive">
            <Table className="modern-table" hover>
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>ID</th>
                  <th>Customer</th>
                  <th>Contact Details</th>
                  <th>Demographics</th>
                  <th>Location</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersList.map((user) => (
                  <tr key={user.userId}>
                    <td className="fw-bold text-success" style={{ fontSize: '1.05rem' }}>#{user.userId}</td>
                    <td>
                      <div className="user-avatar-wrapper">
                        <div className="user-avatar">
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                        <div className="text-start">
                          <div className="fw-bold" style={{ color: '#2d3748', fontSize: '1.05rem' }}>
                            {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ")}
                          </div>
                          {user.isActive ? 
                            <Badge bg="success" className="rounded-pill mt-2 px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}>Active</Badge> : 
                            <Badge bg="secondary" className="rounded-pill mt-2 px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}>Inactive</Badge>
                          }
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column text-start">
                        <div className="text-dark fw-bold mb-1" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faEnvelope} className="me-2 text-success" style={{ width: '18px' }} />
                          {user.email || 'N/A'}
                        </div>
                        <div className="text-dark fw-bold" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faPhone} className="me-2 text-success" style={{ width: '18px' }} />
                          {user.countryCode} {user.mobile || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column text-start">
                        <div className="text-dark fw-bold mb-1 text-capitalize" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faUserEdit} className="me-2 text-info" style={{ width: '18px' }} />
                          {user.gender || 'N/A'}
                        </div>
                        <div className="text-dark fw-bold" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-warning" style={{ width: '18px' }} />
                          {user.dob ? new Date(user.dob).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="text-dark fw-bold text-start" style={{ fontSize: '1rem' }}>
                         <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-danger" style={{ width: '18px' }} /> 
                         {user.city ? `${user.city}, ${user.state}` : 'N/A'}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        <Button
                          variant={user.isActive ? "outline-warning" : "outline-success"}
                          className="icon-btn border-0 shadow-sm"
                          title={user.isActive ? "Deactivate User" : "Activate User"}
                          onClick={() => toggleActivation(user.userId, { ...user, isActive: !user.isActive })}
                        >
                          <FontAwesomeIcon icon={user.isActive ? faBan : faCheckCircle} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          className="icon-btn border-0 shadow-sm"
                          title="Delete User"
                          onClick={() => onDeleteUser(user.userId)}
                        >
                          <FontAwesomeIcon icon={faTrash as any} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {usersList.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-muted">
                      No customers found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Mobile Custom Card View */}
      <div className="d-block d-md-none pb-4">
        {usersList.map((user) => (
          <div key={`mob-${user.userId}`} className="mobile-inspiration-card">
            <div className="mobile-card-header">
              <div className="mobile-card-icon" style={{ background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)', color: 'white' }}>
                <FontAwesomeIcon icon={faUser} />
              </div>
              
              {user.isActive ? 
                <Badge bg="success" className="mobile-card-badge shadow-sm">Active</Badge> : 
                <Badge bg="secondary" className="mobile-card-badge shadow-sm">Inactive</Badge>
              }

              <div>
                <Button
                  variant="light"
                  size="sm"
                  className={`border-0 shadow-none bg-transparent p-1 me-1 text-${user.isActive ? 'warning' : 'success'}`}
                  title={user.isActive ? "Deactivate User" : "Activate User"}
                  onClick={() => toggleActivation(user.userId, { ...user, isActive: !user.isActive })}
                >
                  <i className={`fas ${user.isActive ? 'fa-ban' : 'fa-check-circle'} fa-lg`}></i>
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  className="border-0 shadow-none bg-transparent p-1 text-danger"
                  title="Delete User"
                  onClick={() => onDeleteUser(user.userId)}
                >
                  <i className="fas fa-trash fa-lg"></i>
                </Button>
              </div>
            </div>
            
            <div className="mobile-card-title">
              {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ")}
            </div>
            <div className="mobile-card-subtitle">
              {user.email || 'No Email'}
            </div>
            <div className="mobile-card-desc">
              Phone: {user.countryCode} {user.mobile || 'N/A'} <br/>
              Location: {user.city ? `${user.city}, ${user.state}` : 'N/A'}
            </div>
            
            <div className="mobile-card-pills">
              {user.gender && (
                <span className="mobile-pill text-capitalize">
                  {user.gender}
                </span>
              )}
              {user.dob && (
                <span className="mobile-pill">
                  DOB: {new Date(user.dob).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        ))}
        {usersList.length === 0 && (
          <div className="text-center py-5 text-muted">
            No customers found matching your criteria.
          </div>
        )}
      </div>
      
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center bg-white p-3 rounded-3 shadow-sm mb-4">
        <ViewItemsPerPage
          selectedPageSize={usersListPageSize}
          onSelectPageSize={onChangePageSize}
        />
        <div className="mt-3 mt-md-0">
          <Pagination
            activePage={usersListPageIndex + 1}
            totalItemsCount={usersListCount}
            itemsCountPerPage={usersListPageSize}
            onChange={onChangePageIndex}
            itemClass="page-item"
            linkClass='page-link shadow-none'
            firstPageText="First"
            lastPageText="Last"
          />
        </div>
      </div>
    </div>
  );
};

export default ListUsers;
